import React, { useContext, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import MuiAlert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Add from '@mui/icons-material/Add';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Close from '@mui/icons-material/Close';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Alert, Button } from '@/ui';
import ZuulLoader from '../../ZuulLoader/ZuulLoader';
import PropertyField from '../Properties/PropertyField';
import { CustomPropertiesProps, PropertySetsValues, PropertySetValues, SelectedNode } from './types';
import { useProperties } from '../../../views/CustomProperties/Properties/queries';
import './style.css';
import CustomPropertiesPreviewButton from './preview/CustomPropertiesPreviewButton';
import { getDefaultValue } from '../../../views/CustomProperties/Properties/types';
import { usePropertySets } from '../../../views/CustomProperties/PropertySets/queries';
import { PropertySet } from '../../../views/CustomProperties/PropertySets/types';
import { useDynamicPropertyLoader } from '../ListProperties/queries';
import { isProperty } from '../ListProperties/types';
import { AddPropertySetModalButton } from '../PropertySets/modal/AddPropertySetModal.tsx';
import { isEqual, set, get } from 'lodash';
import { initializePropertySetsValues } from './initializePropertySetsValues';

/**
 * The tree's nodes are mutually recursive and every one of them needs the selection and the
 * values, so they read both from context. Defining them inside `CustomProperties` gave them a
 * fresh identity on every render, which remounted the whole tree each time a property value
 * changed — losing each node's open state and rebuilding the DOM on every keystroke.
 */
interface TreeContextValue {
  selected: SelectedNode | null;
  setSelected: (node: SelectedNode | null) => void;
  propertySetsValues: PropertySetsValues;
  setPropertySetsValues: (propertySetsValues: {}) => void;
  removePropertySet: (propertySet: PropertySet) => void;
}

const TreeContext = React.createContext<TreeContextValue | null>(null);

const useTree = () => {
  const context = useContext(TreeContext);
  if (!context) throw new Error('CustomProperties tree nodes must render inside CustomProperties');
  return context;
};

const DetailsHeader: React.FC<{ title: string; description?: string }> = ({ title, description }) => (
  <div className='details-pane-header'>
    <Typography variant='h5' component='h5' className='details-pane-title'>
      {title}
    </Typography>
    <div className='details-pane-description'>{description}</div>
  </div>
);

/**
 * The properties of one selected set. It lives in its own component because it owns hooks: when
 * these sat below `DetailsPane`'s early returns the hook order changed with the selection, which
 * React reported as "Expected static flag was missing".
 */
const SetDetails: React.FC<{
  setDef: PropertySet;
  path: string;
  propertySetsValues: PropertySetsValues;
  setPropertySetsValues: (propertySetsValues: {}) => void;
}> = ({ setDef, path, propertySetsValues, setPropertySetsValues }) => {
  const { data: propsInSet, isLoading } = useProperties(setDef.setProperties.properties, false);

  const shouldShowProperty = (propertyKey: string) => {
    const renderSettings = setDef?.additionalConfiguration?.dynamicDisplaySettings?.properties?.[propertyKey];
    if (renderSettings) {
      const value = get(propertySetsValues, `${renderSettings.replacementKey}`)?.value;
      if (value && renderSettings.condition === 'is') {
        return renderSettings.value == value;
      } else if (renderSettings.condition === 'is not') {
        return renderSettings.value != value;
      }
    }
    return true;
  };

  return (
    <Card>
      <CardHeader title={<DetailsHeader title={setDef.name} description={setDef.description} />} />
      <CardContent>
        {isLoading || !propsInSet ? (
          <ZuulLoader text={'Loading Property Details'} />
        ) : (
          <div className='property-fields-container'>
            {propsInSet.map((property) => {
              const fieldKey = `${path.replaceAll(/[.\[\]]/g, '_')}-${setDef.replacementKey.replace(
                /[^a-zA-Z0-9]/g,
                '_'
              )}-${setDef.id}-${property.replacementKey.replace(/[^a-zA-Z0-9]/g, '_')}-${property.id}`;

              if (!shouldShowProperty(property.replacementKey)) {
                return;
              }

              return (
                <PropertyField
                  key={fieldKey}
                  property={property}
                  propertyKey={property.replacementKey}
                  value={get(propertySetsValues, `${path}.${property.replacementKey}.value`)}
                  setValue={(propertyKey: string, value: any) => {
                    const updatedValue = set({ ...propertySetsValues }, `${path}.${propertyKey}`, {
                      value,
                      overrideByURL: false,
                    });
                    setPropertySetsValues(updatedValue);
                  }}
                  fieldKey={fieldKey}
                />
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const DetailsPane: React.FC<{
  sel: SelectedNode | null;
  propertySetsValues: PropertySetsValues;
  setPropertySetsValues: (propertySetsValues: {}) => void;
}> = ({ sel, propertySetsValues, setPropertySetsValues }) => {
  if (!sel) {
    return (
      <MuiAlert severity='info' variant='outlined'>
        Select a set or list item to edit its properties
      </MuiAlert>
    );
  }

  if (sel.kind === 'set' && sel.set.setProperties.properties.length === 0) {
    return (
      <MuiAlert severity='info' variant='outlined'>
        This set has no properties to configure.
      </MuiAlert>
    );
  }

  if (sel.kind === 'listItemProperty') {
    const { property } = sel;

    return (
      <Card>
        <CardHeader title={<DetailsHeader title={property.label} description={property.description} />} />
        <CardContent>
          <PropertyField
            property={property}
            propertyKey={property.replacementKey}
            value={get(propertySetsValues, `${sel.path}.${property.replacementKey}.value`)}
            setValue={(propertyKey: string, value: any) => {
              const updated = set({ ...propertySetsValues }, `${sel.path}.${propertyKey}`, {
                value,
                overrideByURL: false,
              });
              setPropertySetsValues(updated);
            }}
            fieldKey={`${sel.path.replaceAll(/[.\[\]]/g, '_')}-${property.replacementKey}-${property.id}`}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <SetDetails
      setDef={sel.set}
      path={sel.path}
      propertySetsValues={propertySetsValues}
      setPropertySetsValues={setPropertySetsValues}
    />
  );
};

const RootSetActions: React.FC<{
  setDef: PropertySet;
  propertySetsValues: PropertySetsValues;
  path: string;
  removePropertySet: (propertySet: PropertySet) => void;
}> = ({ setDef, propertySetsValues, path, removePropertySet }) => {
  return (
    <>
      <CustomPropertiesPreviewButton values={{ [setDef.replacementKey]: { ...get(propertySetsValues, path) } }} />
      <Button
        color='danger'
        size='sm'
        title='Remove set'
        aria-label='Remove set'
        onClick={(e) => {
          e.stopPropagation();
          removePropertySet(setDef);
        }}
      >
        <Close fontSize='small' />
      </Button>
    </>
  );
};

const TreeSetNode: React.FC<{
  path: string;
  setDef: PropertySet;
  isRoot?: boolean;
}> = ({ path, setDef, isRoot }) => {
  const { selected, setSelected, propertySetsValues, removePropertySet } = useTree();
  const isActive = (nodePath: string) => selected?.path === nodePath;

  // Prevent circular references rendering
  const isCircular = path?.split('.').filter((key) => key === setDef.replacementKey).length > 1;

  const { isLoading: loadingProps } = useProperties(isCircular ? [] : setDef.setProperties.properties, false);
  const { data: childSets, isLoading: loadingSets } = usePropertySets(
    isCircular ? [] : setDef.setProperties.propertySets,
    false
  );

  const [open, setOpen] = useState<boolean>(selected?.path?.startsWith(path) || false);

  if (isCircular) {
    return (
      <Alert color='danger'>
        <Typography variant='h4' component='h4'>
          Circular reference detected!
        </Typography>
        <p>
          looks like this Property Set references another set that&apos;s higher in the chain.
          <br />
          Please check the configuration.
        </p>
        <Divider sx={{ my: 1 }} />
        <b>Property Set ID:</b> {setDef.id}
        <br />
        <b>Replacement Key:</b> {setDef.replacementKey}
        <br />
        <b>Path:</b> {path}
      </Alert>
    );
  }

  const shouldShowSet = (setKey: string) => {
    const renderSettings = setDef?.additionalConfiguration?.dynamicDisplaySettings?.sets?.[setKey];
    if (renderSettings) {
      const value = get(propertySetsValues, `${renderSettings.replacementKey}`)?.value;

      if (!value) return true;

      if (renderSettings.condition === 'is') {
        return renderSettings.value == value;
      } else if (renderSettings.condition === 'is not') {
        return renderSettings.value != value;
      }
    }
    return true;
  };

  return (
    <div
      className={`tree-node ${isRoot ? '' : 'nested'} ${
        isRoot && open && childSets && childSets.length > 0 ? 'active-set' : ''
      }`}
    >
      <div
        className={`tree-node-header ${isActive(path) ? 'active' : ''}`}
        onClick={() => {
          setSelected({ kind: 'set', path, set: setDef });
          // Selecting a set also expands it. This used to happen by accident, when every render
          // remounted the tree and re-ran the `open` initializer; now that nodes keep their state,
          // it has to be done here.
          setOpen(true);
        }}
        onAuxClick={() => {
          if (setDef.id) {
            window.open('/custom-properties/property-sets/' + setDef.id, '_blank');
          }
        }}
      >
        <div className='tree-node-content'>
          <div className='tree-node-icon'>
            {childSets && childSets.length > 0 ? (
              <Box
                component='span'
                className='tree-node-caret'
                sx={{ display: 'inline-flex' }}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(!open);
                }}
              >
                {open ? <ExpandMore fontSize='small' /> : <ChevronRight fontSize='small' />}
              </Box>
            ) : (
              <span className='tree-node-spacer'></span>
            )}
          </div>
          <span>{setDef.name}</span>
        </div>
        {isRoot && (
          <div className='tree-node-actions'>
            <RootSetActions
              setDef={setDef}
              propertySetsValues={propertySetsValues}
              path={path}
              removePropertySet={removePropertySet}
            />
          </div>
        )}
      </div>
      {open && (
        <div className={`tree-children ${isActive(path) ? 'active' : ''}`}>
          {loadingProps || loadingSets ? (
            <ZuulLoader text={'Loading'} />
          ) : (
            <>
              {childSets?.map(
                (child) =>
                  shouldShowSet(child.replacementKey) && (
                    <TreeSetNode
                      key={`${path}.${child.replacementKey}`}
                      path={`${path}.${child.replacementKey}`}
                      setDef={child}
                    />
                  )
              )}
              {setDef.setProperties.listProperties?.map(
                (lp) =>
                  shouldShowSet(lp.replacementKey) && (
                    <ListPropertyNode key={`${path}.${lp.replacementKey}`} parentPath={path} listProperty={lp} />
                  )
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const ListPropertyNode: React.FC<{
  parentPath: string;
  listProperty: any; // keep loose to avoid new types here
}> = ({ parentPath, listProperty }) => {
  const { selected, setSelected, propertySetsValues, setPropertySetsValues } = useTree();
  const isActive = (nodePath: string) => selected?.path === nodePath;
  const { data: element } = useDynamicPropertyLoader(listProperty.elementId, listProperty.listType);
  const items = useMemo(
    () => (get(propertySetsValues, `${parentPath}.${listProperty.replacementKey}`, []) as PropertySetValues[]) || [],
    [propertySetsValues, parentPath, listProperty.replacementKey]
  );

  const onAdd = () => {
    if (!element) return;
    const next = [...items];
    if (isProperty(element)) {
      next.push({
        [element.replacementKey]: {
          value: element.additionalConfiguration?.defaultValue || getDefaultValue(element.type),
          overrideByURL: false,
        },
      });
    } else {
      next.push({});
    }
    const updated = set({ ...propertySetsValues }, `${parentPath}.${listProperty.replacementKey}`, next);
    setPropertySetsValues(updated);
  };

  const onRemove = (idx: number) => {
    const next = items.filter((_, i) => i !== idx);
    const updated = set({ ...propertySetsValues }, `${parentPath}.${listProperty.replacementKey}`, next);
    setPropertySetsValues(updated);
    // Clear selection if the removed item was selected
    if (selected?.path.startsWith(`${parentPath}.${listProperty.replacementKey}[${idx}]`)) {
      setSelected(null);
    }
  };

  const [open, setOpen] = useState(true);

  return (
    <div className='list-property-node'>
      <div className='list-property-header'>
        <div className='list-property-content'>
          <Box
            component='span'
            className='tree-node-caret'
            sx={{ display: 'inline-flex' }}
            onClick={() => setOpen(!open)}
          >
            {open ? <ExpandMore fontSize='small' /> : <ChevronRight fontSize='small' />}
          </Box>
          <span>{listProperty.name}</span>
          <span className='list-property-count'>{`(${items.length} items)`}</span>
        </div>
        <Button color='success' size='sm' onClick={onAdd} title='Add item' aria-label='Add item'>
          <Add fontSize='small' />
        </Button>
      </div>
      {open && (
        <div>
          {items.map((_, idx) => {
            if (element && isProperty(element)) {
              const itemPath = `${parentPath}.${listProperty.replacementKey}[${idx}]`;
              return (
                <div
                  key={itemPath}
                  className={`list-item-node ${isActive(itemPath) ? 'active' : ''}`}
                  onClick={() =>
                    setSelected({
                      kind: 'listItemProperty',
                      path: itemPath,
                      property: element,
                      listKey: listProperty.replacementKey,
                      index: idx,
                    })
                  }
                >
                  <div className='list-item-content'>
                    <Chip size='small' label={idx} />
                    <span>{listProperty.name}</span>
                  </div>
                  <div className='list-item-actions'>
                    <Button
                      color='danger'
                      size='sm'
                      title='Remove item'
                      aria-label='Remove item'
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemove(idx);
                      }}
                    >
                      <DeleteOutlined fontSize='small' />
                    </Button>
                  </div>
                </div>
              );
            } else if (element) {
              const childSet = element as PropertySet;
              const childPath = `${parentPath}.${listProperty.replacementKey}[${idx}].${childSet.replacementKey}`;
              return (
                <div key={childPath} className='list-property-set-container'>
                  <div className='list-property-set-header'>
                    {open ? <ExpandMore fontSize='small' /> : <ChevronRight fontSize='small' />}
                    <Chip size='small' label={idx} />
                    <span>{`${listProperty.name}`}</span>
                    <div className='list-item-actions'>
                      <Button
                        color='danger'
                        size='sm'
                        title='Remove item'
                        aria-label='Remove item'
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemove(idx);
                        }}
                      >
                        <DeleteOutlined fontSize='small' />
                      </Button>
                    </div>
                  </div>
                  <TreeSetNode path={childPath} setDef={childSet} />
                </div>
              );
            }
            return null;
          })}
        </div>
      )}
    </div>
  );
};

export const CustomProperties = ({
  propertySetsIds,
  propertySets,
  propertySetsValues,
  setPropertySetsValues,
  removePropertySet,
  setPropertySets,
  setPropertySetsIds,
}: CustomPropertiesProps) => {
  const [selected, setSelected] = useState<SelectedNode | null>(() =>
    propertySets && propertySets.length > 0 && propertySets[0]
      ? { kind: 'set', path: propertySets[0].replacementKey, set: propertySets[0] }
      : null
  );

  const { data: allProperties } = useProperties();
  const { data: allPropertySets } = usePropertySets();
  const definitions = useMemo(
    () =>
      allProperties && allPropertySets
        ? {
            propertiesById: new Map(allProperties.map((p) => [p.id as number, p])),
            propertySetsById: new Map(allPropertySets.map((s) => [s.id as number, s])),
          }
        : null,
    [allProperties, allPropertySets]
  );

  // Save every attached set's defaults — nested sets included — as soon as the set is attached,
  // in one update. Initializing per selected set left them unsaved until someone clicked the
  // set, and its two separate updates raced, so the set was saved as `{}` (ZET-4907).
  useEffect(() => {
    if (!definitions || propertySets.length === 0) return;
    const initialized = initializePropertySetsValues(propertySets, propertySetsValues, definitions);
    if (!isEqual(initialized, propertySetsValues)) {
      setPropertySetsValues(initialized);
    }
  }, [definitions, propertySets, propertySetsValues, setPropertySetsValues]);

  const tree = useMemo(
    () => ({ selected, setSelected, propertySetsValues, setPropertySetsValues, removePropertySet }),
    [selected, propertySetsValues, setPropertySetsValues, removePropertySet]
  );

  return (
    <TreeContext.Provider value={tree}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 5, lg: 4 }} sx={{ mb: 3 }}>
          <Card>
            <CardHeader
              title={
                // The 5/7 split the Row/Col header had: the title needs its share or it wraps
                // mid-phrase in this narrow column.
                <Grid container spacing={1} sx={{ alignItems: 'center' }}>
                  <Grid size={5}>
                    <DetailsHeader title='Property Sets' description={`${propertySets.length} property sets`} />
                  </Grid>
                  <Grid size={7} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <AddPropertySetModalButton
                      buttonLabel={'Add Custom Property'}
                      alreadyAddedPropertySets={propertySets}
                      onAddPropertySet={(propertySet) => {
                        setPropertySets([...(propertySets || []), propertySet]);
                        setPropertySetsIds([...(propertySetsIds || []), propertySet.id]);
                      }}
                    />
                  </Grid>
                </Grid>
              }
              slotProps={{ title: { component: 'div' } }}
            />
            <CardContent sx={{ p: 0.5, '&:last-child': { pb: 0.5 } }}>
              {propertySets.map((propertySet) => (
                <div
                  key={propertySet.id}
                  className={`${
                    selected?.path?.startsWith(propertySet.replacementKey) &&
                    selected?.path !== propertySet.replacementKey
                      ? 'active-set'
                      : ''
                  }`}
                >
                  <TreeSetNode path={propertySet.replacementKey} setDef={propertySet} isRoot />
                </div>
              ))}
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 7, lg: 8 }}>
          <DetailsPane
            sel={selected}
            setPropertySetsValues={setPropertySetsValues}
            propertySetsValues={propertySetsValues}
          />
        </Grid>
      </Grid>
    </TreeContext.Provider>
  );
};
